import { ImageUrlInput } from "@/components/media/ImageUrlInput";
import { MediaPickerButton } from "@/components/media/MediaPickerButton";
import { useDeviceType } from "@/hooks/use-device-type";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { productVariantService } from "@/services/product-variant-service";
import { ProductVariant } from "@/types/product-type";
import {
  ProductVariantCreateFormValues,
  ProductVariantUpdateFormValues,
  productVariantCreateSchema,
  productVariantUpdateSchema,
} from "@/types/product-variant-type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

// ─── shared option lists (mirrors ProductAttributesFormSection) ──────────────

const colorOptions = [
  { label: "Black / ពណ៌ខ្មៅ", value: "Black", colorCode: "#000000" },
  { label: "White / ពណ៌ស", value: "White", colorCode: "#FFFFFF" },
  { label: "Gray / ពណ៌ប្រផេះ", value: "Gray", colorCode: "#808080" },
  { label: "Red / ពណ៌ក្រហម", value: "Red", colorCode: "#FF0000" },
  { label: "Green / ពណ៌បៃតង", value: "Green", colorCode: "#008000" },
  { label: "Blue / ពណ៌ខៀវ", value: "Blue", colorCode: "#0000FF" },
  { label: "Yellow / ពណ៌លឿង", value: "Yellow", colorCode: "#FFFF00" },
  { label: "Orange / ពណ៌ទឹកក្រូច", value: "Orange", colorCode: "#FFA500" },
  { label: "Purple / ពណ៌ស្វាយ", value: "Purple", colorCode: "#800080" },
  { label: "Pink / ពណ៌ផ្កាឈូក", value: "Pink", colorCode: "#FFC0CB" },
  { label: "Brown / ពណ៌ត្នោត", value: "Brown", colorCode: "#A52A2A" },
  { label: "Navy / ពណ៌ខៀវចាស់", value: "Navy", colorCode: "#000080" },
  { label: "Beige / ពណ៌បៃតងស្រាលត្នោត", value: "Beige", colorCode: "#F5F5DC" },
  { label: "Khaki / ពណ៌កាគី", value: "Khaki", colorCode: "#C3B091" },
  { label: "Maroon / ពណ៌ក្រហមចាស់", value: "Maroon", colorCode: "#800000" },
];

const sizeOptions = [
  { label: "XXS", value: "XXS" },
  { label: "XS", value: "XS" },
  { label: "S", value: "S" },
  { label: "M", value: "M" },
  { label: "L", value: "L" },
  { label: "XL", value: "XL" },
  { label: "XXL", value: "XXL" },
  { label: "XXXL", value: "XXXL" },
];

// Small colour dot swatch
function ColorSwatch({ colorCode }: { colorCode: string }) {
  return (
    <span
      className="inline-block w-3.5 h-3.5 rounded-full shrink-0 border"
      style={{
        backgroundColor: colorCode,
        borderColor: colorCode === "#FFFFFF" ? "#d1d5db" : "transparent",
      }}
    />
  );
}

// ─── component ───────────────────────────────────────────────────────────────

type VariantFormSubmitHandler = (
  data: ProductVariantCreateFormValues | ProductVariantUpdateFormValues
) => Promise<void> | void;

interface VariantFormProps {
  mode: "create" | "edit";
  productId: string;
  productCode: string;
  editingVariant?: ProductVariant | null;
  onSubmit: VariantFormSubmitHandler;
  onCancel: () => void;
  isLoading?: boolean;
}

export function VariantForm({
  mode,
  productId,
  productCode,
  editingVariant,
  onSubmit,
  onCancel,
  isLoading = false,
}: VariantFormProps) {
  const { isMobile } = useDeviceType();
  const form = useForm<
    ProductVariantCreateFormValues | ProductVariantUpdateFormValues
  >({
    resolver: zodResolver(
      mode === "create" ? productVariantCreateSchema : productVariantUpdateSchema
    ),
    defaultValues:
      mode === "create"
        ? {
          storeId: 1,
          productId,
          sku: "",
          name: "",
          price: 0,
          cost: 0,
          imageUrl: "",
          color: null,
          size: null,
        }
        : {
          sku: editingVariant?.sku || "",
          name: editingVariant?.name || "",
          price: editingVariant?.price || 0,
          cost: editingVariant?.cost || 0,
          imageUrl: editingVariant?.imageUrl || "",
          color: editingVariant?.color ?? null,
          size: editingVariant?.size ?? null,
        },
  });

  const handleSubmit = async (data: any) => {
    if (mode === "create") form.setValue("storeId", 1);
    // Normalise empty-string → null
    if (data.color === "" || data.color === "_none") data.color = null;
    if (data.size === "" || data.size === "_none") data.size = null;
    await onSubmit(data);
  };

  const generateSKU = () => {
    const variantName = form.getValues("name");
    if (variantName && productCode) {
      form.setValue("sku", productVariantService.generateSKU(productCode, variantName));
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit(handleSubmit)(e);
        }}
        className="space-y-6"
      >
        {/* ── Basic Information ──────────────────────────────────────── */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Variant Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Red Large" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="sku"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>SKU (Use for live sale code.)</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Input placeholder="e.g. SKU-001" {...field} />
                      {mode === "create" && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={generateSKU}
                        >
                          Generate
                        </Button>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="imageUrl"
              render={({ field }) => {
                const setUrl = (url: string) =>
                  form.setValue("imageUrl", url, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                return (
                  <FormItem>
                    <FormLabel>Image (Optional)</FormLabel>
                    <FormControl>
                      {/*
                        Mobile:  ImageUrlInput full-width (tap-to-upload zone),
                                 MediaPickerButton below as secondary action.
                        Desktop: Side-by-side row.
                      */}
                      <div className={isMobile ? "flex flex-col gap-2" : "flex gap-2 items-center"}>
                        <ImageUrlInput
                          value={field.value ?? ""}
                          onChange={setUrl}
                          placeholder="https://... or drag & drop an image"
                          className="flex-1"
                        />
                        <div className={isMobile ? "self-start" : "shrink-0"}>
                          <MediaPickerButton
                            label={isMobile ? "Browse library" : "Choose image"}
                            multi={false}
                            allowUpload
                            previewUrl={isMobile ? undefined : (field.value || undefined)}
                            onClear={() => setUrl("")}
                            onPick={(items) => {
                              const url = items[0]?.s3Url || items[0]?.fileUrl || "";
                              setUrl(url);
                            }}
                          />
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
        </div>

        {/* ── Color & Size ───────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Color — shadcn Select (Radix portal, correct z-index inside dialog) */}
          <FormField
            control={form.control}
            name={"color" as any}
            render={({ field }) => {
              const val: string = field.value ?? "";
              const selected = colorOptions.find((o) => o.value === val);
              return (
                <FormItem>
                  <FormLabel>Color (Optional)</FormLabel>
                  <Select
                    value={val || "_none"}
                    onValueChange={(v) => field.onChange(v === "_none" ? null : v)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select color…">
                          {selected ? (
                            <span className="flex items-center gap-2">
                              <ColorSwatch colorCode={selected.colorCode} />
                              {selected.label}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Select color…</span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="_none">
                        <span className="text-muted-foreground italic">None</span>
                      </SelectItem>
                      {colorOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="flex items-center gap-2">
                            <ColorSwatch colorCode={opt.colorCode} />
                            {opt.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Size — shadcn Select */}
          <FormField
            control={form.control}
            name={"size" as any}
            render={({ field }) => {
              const val: string = field.value ?? "";
              return (
                <FormItem>
                  <FormLabel>Size (Optional)</FormLabel>
                  <Select
                    value={val || "_none"}
                    onValueChange={(v) => field.onChange(v === "_none" ? null : v)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select size…">
                          {val && val !== "_none" ? (
                            <span className="font-medium">{val}</span>
                          ) : (
                            <span className="text-muted-foreground">Select size…</span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="_none">
                        <span className="text-muted-foreground italic">None</span>
                      </SelectItem>
                      {sizeOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        {/* ── Pricing ────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Price (USD)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    placeholder="20.00"
                    value={
                      field.value === 0 || field.value === undefined ? "" : field.value
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        field.onChange(undefined as unknown as number);
                        return;
                      }
                      const parsed = Number(raw);
                      if (!Number.isNaN(parsed)) field.onChange(parsed);
                    }}
                    onBlur={(e) => {
                      if (e.target.value === "") field.onChange(0);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cost"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cost (USD)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    placeholder="10.00"
                    value={
                      field.value === 0 || field.value === undefined ? "" : field.value
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        field.onChange(undefined as unknown as number);
                        return;
                      }
                      const parsed = Number(raw);
                      if (!Number.isNaN(parsed)) field.onChange(parsed);
                    }}
                    onBlur={(e) => {
                      if (e.target.value === "") field.onChange(0);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* ── Actions ────────────────────────────────────────────────── */}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? mode === "create"
                ? "Creating..."
                : "Updating..."
              : mode === "create"
                ? "Create Variant"
                : "Update Variant"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
